import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import { ModelName, QueryTag, TagList } from '@casops/store/api';
import { Permission } from '@casops/permissions';
import CollectionView from '@casops/components/layout/collection-view/CollectionView';
import { useApprovalColDefs } from './useApprovalColDefs';
import { useTableData } from '@casops/shared/hooks/useTableData';
import {
  AssetApprovalBasicModel,
  useListApprovalsQuery,
} from '@casops/store/apis/assetApprovalsApi';
import { TagDescription } from '@reduxjs/toolkit/query';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { SidePanel } from '@casops/store/slices/globalSlice';

type Model = AssetApprovalBasicModel;

const modelName = ModelName.PendingApproval;
const permission = Permission.AssetManagement;

const tagsToClearOnRefresh: TagDescription<QueryTag>[] = [
  { type: QueryTag.AssetApproval, id: TagList },
  { type: QueryTag.ConfigurationGroup, id: TagList }, // table, form
  { type: QueryTag.AssetGroup, id: TagList }, // form
];

export function PendingApprovalPage() {
  const { t } = useTranslation();

  const columnDefs = useApprovalColDefs();

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListApprovalsQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <StandardPage
      header={
        <PageHeader
          title={t('Model.Collection.Title', { modelName })}
          tagsToClearOnRefresh={tagsToClearOnRefresh}
        />
      }
    >
      <CollectionView<Model>
        sliceKey={modelName}
        modelName={modelName}
        secondaryModelName={ModelName.Asset}
        columnDefs={columnDefs}
        rowData={data}
        hasData={hasData}
        noDataDescription={noDataDescription}
        actions={
          <CreateItemButton
            modelName={modelName}
            sidePanel={SidePanel.PreApprovalImport}
            permission={permission}
            label={t('ImportPreApprovals')}
            icon="arrow-down"
          />
        }
      />
    </StandardPage>
  );
}

export default PendingApprovalPage;
